// The command bar — the player's whole input surface after P3.
//
// It lives in the bottom letterbox band rather than over the paddock, which is the fix for the
// original complaint: the dog is no longer under the thumb because the thumb is no longer on the
// dog. See PLAN.md § Playtest feedback P3.
//
// Buttons are built from `COMMANDS` rather than written out here, so the vocabulary has exactly one
// source of truth and a seventh command cannot appear in the engine without appearing on screen.
//
// The bar shows which order is STANDING. That is not decoration: a standing order persists until it
// is countermanded, so a player who cannot see the current order cannot tell the difference between
// a dog that is flanking and a dog that has finished flanking — and there is no difference except
// what the dog does next.

import { COMMANDS, COMMAND_LABELS, type CommandType } from '../engine/commands'
import { BARK_GLYPH, COMMAND_GLYPHS } from './glyphs'
import { keyHint } from './keyboard'

/** Bark is not a handler command, but it is the other thing a player can ask for. */
export type BarAction = CommandType | 'bark'

interface Entry {
  action: BarAction
  el: HTMLButtonElement
}

export class CommandBar {
  private readonly entries: Entry[] = []

  constructor(
    private readonly root: HTMLElement,
    onAction: (action: BarAction) => void,
  ) {
    const add = (
      action: BarAction,
      label: string,
      glyph: string,
      className: string,
      hint = '',
    ): void => {
      const el = document.createElement('button')
      el.type = 'button'
      el.className = className
      el.dataset.action = action
      // Glyph first: the bar is read under time pressure with a thumb over it, and a shape lands
      // faster than a word. The label stays — the glyphs teach the vocabulary rather than replace
      // it, and a player who has never worked a dog has no idea what "away" means yet.
      // The hint is decorative for assistive tech — the aria-label already names the command, and a
      // screen reader announcing "1" before every button is noise.
      const hintMark = hint ? `<span class="cmd-key" aria-hidden="true">${hint}</span>` : ''
      el.innerHTML = `${hintMark}${glyph}<span class="cmd-label">${label}</span>`
      el.setAttribute('aria-label', label)
      // `pointerdown` rather than `click`: on touch, click waits for the gesture to resolve and
      // adds ~100ms+ before the dog reacts. This is a real-time game against a par time, and a
      // command that lands a tick late is a command the player did not give.
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault()
        onAction(action)
      })
      root.appendChild(el)
      this.entries.push({ action, el })
    }

    for (const c of COMMANDS) add(c, COMMAND_LABELS[c], COMMAND_GLYPHS[c], 'cmd', keyHint(c))
    add('bark', 'BARK', BARK_GLYPH, 'cmd cmd-bark', 'B')
  }

  /**
   * Reflect the sim's current standing order. Driven from the render callback rather than from the
   * click, so what is lit is what the ENGINE believes — a button that lights on click would keep
   * lying if the event were ever dropped or countermanded.
   */
  setActive(
    order: CommandType | null,
    lifted = false,
    pressSteps = 0,
    steady = false,
    backSteps = 0,
  ): void {
    for (const e of this.entries) {
      if (e.action === 'bark') continue

      // The two modifiers are not the standing order and must not be lit as though they were — the
      // dog can be steady AND flanking. They get their own state.
      if (e.action === 'steady') {
        e.el.classList.toggle('is-modifier-on', steady)
        e.el.setAttribute('aria-pressed', String(steady))
        continue
      }
      if (e.action === 'get-back') {
        e.el.classList.toggle('is-modifier-on', backSteps > 0)
        e.el.style.setProperty('--press', String(backSteps))
        e.el.setAttribute('aria-pressed', String(backSteps > 0))
        continue
      }

      const on = e.action === order
      // How hard the dog is leaning, shown as intensity. Pressure the player cannot see is pressure
      // they cannot manage, and stress is already the invisible half of the score.
      if (e.action === 'walk-on') {
        e.el.style.setProperty('--press', String(on ? pressSteps : 0))
      }
      e.el.classList.toggle('is-active', on)
      // A lifted flank has arrived and stopped at the balance point. Showing it is not decoration:
      // pressing the same command again is what sends the dog round a second time, and a player
      // who cannot see that the flank finished has no way to discover the override exists.
      e.el.classList.toggle('is-lifted', on && lifted)
      e.el.setAttribute('aria-pressed', String(on))
    }
  }

  /** Barks are on a cooldown in the sim; showing it stops the button reading as broken. */
  setBarkReady(ready: boolean): void {
    const bark = this.entries.find((e) => e.action === 'bark')
    if (bark) bark.el.classList.toggle('is-spent', !ready)
  }

  setEnabled(enabled: boolean): void {
    this.root.classList.toggle('is-disabled', !enabled)
    for (const e of this.entries) e.el.disabled = !enabled
  }
}
